import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  FilmIcon,
  TableCellsIcon,
  TrophyIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import Navbar from "../components/navBar";

const navigationGroups = [
  {
    title: "Match centre",
    description: "Follow fixtures and matchday action.",
    items: [
      {
        title: "Live matches",
        description: "Follow matches currently in progress.",
        path: "/live-matches",
        icon: VideoCameraIcon,
        featured: true,
      },
      {
        title: "Upcoming matches",
        description: "See what's scheduled next.",
        path: "/upcoming-matches",
        icon: CalendarDaysIcon,
      },
    ],
  },
  {
    title: "Explore sports",
    description: "Discover teams, competitions and coverage.",
    items: [
      {
        title: "Highlights",
        description: "Revisit recent match moments.",
        path: "/highlights",
        icon: FilmIcon,
      },
      {
        title: "Clubs",
        description: "Browse clubs and their profiles.",
        path: "/clubs",
        icon: BuildingOfficeIcon,
      },
    ],
  },
  {
    title: "Analysis",
    description: "Explore competition standings and player data.",
    items: [
      {
        title: "League tables",
        description: "View current competition standings.",
        path: "/league-tables",
        icon: TableCellsIcon,
      },
      {
        title: "Stats",
        description: "Explore league and player statistics.",
        path: "/stats",
        icon: ChartBarIcon,
      },
    ],
  },
];

const Sports = () => {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";

  return (
    <>
      <Navbar />
      <main
        style={{ "--sports-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--sports-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <TrophyIcon aria-hidden="true" className="h-4 w-4" />
                DESAC SPORTS
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Your sports hub
              </h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Fixtures, match coverage and competition insights—all in one place.
              </p>
            </div>
            <div className="inline-flex w-fit items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 py-2 text-xs font-medium text-gray-400">
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-accent" />
              Explore match coverage
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl space-y-9 px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
          {navigationGroups.map((group) => (
            <section key={group.title} aria-labelledby={`sports-${group.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="mb-4">
                <h2
                  id={`sports-${group.title.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-lg font-semibold tracking-tight text-white sm:text-xl"
                >
                  {group.title}
                </h2>
                <p className="mt-1 text-sm text-gray-400">{group.description}</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {group.items.map(({ title, description, path, icon: Icon, featured }) => (
                  <button
                    key={path}
                    type="button"
                    onClick={() => navigate(path)}
                    aria-label={`Open ${title}`}
                    className={`group flex min-h-28 min-w-0 items-center gap-4 rounded-xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:bg-surface-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:p-5 ${
                      featured
                        ? "border-accent/30 bg-accent/5 hover:border-accent/50"
                        : "border-line bg-surface-850"
                    }`}
                  >
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                        featured
                          ? "border-accent/20 bg-accent/10 text-accent-soft group-hover:bg-accent/15"
                          : "border-line bg-surface-900 text-gray-400 group-hover:text-white"
                      }`}
                    >
                      <Icon aria-hidden="true" className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-white sm:text-base">
                        {title}
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-gray-400 sm:text-sm">
                        {description}
                      </span>
                    </span>
                    <ArrowRightIcon
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 text-gray-500 transition-all group-hover:translate-x-0.5 group-hover:text-accent-soft"
                    />
                  </button>
                ))}
              </div>
            </section>
          ))}

          <div className="flex items-start gap-3 rounded-xl border border-line bg-surface-900 p-4 text-sm text-gray-400 sm:p-5">
            <TrophyIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-accent-soft" />
            <p>Match schedules, scores and standings are shown on their respective pages using the latest available sports data.</p>
          </div>
        </div>
      </main>
    </>
  );
};

export default Sports;
